// ABOUTME: Formats raw Grim Dawn devotion stat ids + values into player-facing rows.
// ABOUTME: Encodes the percent/flat split and GD's internal->display quirks (Life=Vitality, Dexterity=Cunning, ...).
import type { PetInfo } from "./types";
import { appT, gameT, gameStrippedT, litT, joinT, type Text } from "./localization";
import { STAT_TAGS, STAT_FORMAT_TAGS } from "./statTags";

export interface StatRow {
  label: Text;
  value: Text;
  /** The raw stat id the row represents, present when the row is taggable in the benefit filter. */
  id?: string;
}

// Resolve a stat catalog key to display text: mapped keys (data/stat-tags.json) go through the
// authoritative game term (gameT); unmapped keys fall back to the app catalog (appT).
function statLabel(key: string): Text {
  const tag = STAT_TAGS[key];
  return tag ? gameT(tag) : appT(key);
}

// Instant damage type segments recognized in ids. GD quirks: internal Life = Vitality, Poison = Acid.
// Display names live in the catalog under stat.damage.<Segment>, resolved at the read site (never at
// module load, since the localization singleton is installed after this module evaluates).
const INSTANT_DAMAGE_SEGMENTS = new Set([
  "Physical",
  "Pierce",
  "Fire",
  "Cold",
  "Lightning",
  "Elemental",
  "Aether",
  "Chaos",
  "Poison",
  "Life",
]);
function instantDamageLabel(segment: string): Text | undefined {
  return INSTANT_DAMAGE_SEGMENTS.has(segment) ? statLabel(`stat.damage.${segment}`) : undefined;
}
// "Slow" (damage-over-time) type segments. Display names live under stat.dot.<Segment>.
const DOT_DAMAGE_SEGMENTS = new Set(["Bleeding", "Physical", "Fire", "Cold", "Lightning", "Poison", "Life"]);
function dotDamageLabel(segment: string): Text | undefined {
  return DOT_DAMAGE_SEGMENTS.has(segment) ? statLabel(`stat.dot.${segment}`) : undefined;
}
// Resistance type segments. (Status effects like Stun/Freeze are NOT resistances in GD - they are
// duration-reduction stats, handled in OVERRIDES.) Display names live under stat.resist.<Segment>.
const RESIST_SEGMENTS = new Set([
  "Physical",
  "Pierce",
  "Fire",
  "Cold",
  "Lightning",
  "Aether",
  "Chaos",
  "Poison",
  "Life",
  "Bleeding",
]);
function resistLabel(segment: string): Text | undefined {
  return RESIST_SEGMENTS.has(segment) ? statLabel(`stat.resist.${segment}`) : undefined;
}
// Character attribute segments (GD renamed the classic attributes). Display names live under
// stat.attr.<Segment>.
const ATTR_SEGMENTS = new Set([
  "Strength",
  "Dexterity",
  "Intelligence",
  "Life",
  "Mana",
  "OffensiveAbility",
  "DefensiveAbility",
  "LifeRegen",
  "ManaRegen",
]);
function attrLabel(segment: string): Text | undefined {
  return ATTR_SEGMENTS.has(segment) ? statLabel(`stat.attr.${segment}`) : undefined;
}

interface Classified {
  label: Text;
  percent: boolean;
  sign: number; // 1 normal, -1 for reductions shown as negative
}

// Irregular keys that do not follow the family rules below. percent/sign are structural; the display
// label is resolved from the catalog at the read site (stat.override.<id>).
const OVERRIDES: Record<string, { percent: boolean; sign: number }> = {
  defensiveProtection: { percent: false, sign: 1 },
  defensiveProtectionModifier: { percent: true, sign: 1 },
  defensiveAbsorptionModifier: { percent: true, sign: 1 },
  defensiveBlockModifier: { percent: true, sign: 1 },
  defensiveBlockAmountModifier: { percent: true, sign: 1 },
  defensiveElementalResistance: { percent: true, sign: 1 },
  defensiveTotalSpeedResistance: { percent: true, sign: 1 },
  defensivePercentReflectionResistance: { percent: true, sign: 1 },
  defensiveSlowLifeLeach: { percent: true, sign: 1 },
  defensiveSlowManaLeach: { percent: true, sign: 1 },
  defensiveSlowLifeLeachDuration: { percent: true, sign: 1 },
  // Status effects: duration-reduction / protection stats, NOT resistances.
  defensiveStun: { percent: true, sign: 1 },
  defensiveFreeze: { percent: true, sign: 1 },
  defensivePetrify: { percent: true, sign: 1 },
  defensiveTrap: { percent: true, sign: 1 },
  defensiveDisruption: { percent: true, sign: 1 },

  offensiveTotalDamageModifier: { percent: true, sign: 1 },
  offensiveCritDamageModifier: { percent: true, sign: 1 },
  offensiveLifeLeechMin: { percent: true, sign: 1 },
  offensiveSlowManaLeachMin: { percent: false, sign: 1 },
  offensiveSlowManaLeachChance: { percent: true, sign: 1 },
  offensiveSlowManaLeachDurationMin: { percent: false, sign: 1 },
  offensiveElementalResistanceReductionPercentMin: { percent: true, sign: 1 },
  offensiveElementalResistanceReductionPercentDurationMin: { percent: false, sign: 1 },
  offensiveLightningModifierChance: { percent: true, sign: 1 },
  offensiveTargetArmorReduction: { percent: true, sign: 1 },
  retaliationTotalDamageModifier: { percent: true, sign: 1 },
  // retaliationDamagePct is not here: its noun is a value-PREFIX game format
  // (SkillRetaliationDamageFormat), so it resolves via STAT_FORMAT_TAGS instead.
  retaliationFearMin: { percent: false, sign: 1 },
  retaliationFearChance: { percent: true, sign: 1 },

  racialBonusPercentDamage: { percent: true, sign: 1 },
  racialBonusPercentDefense: { percent: true, sign: 1 },
  skillManaCostReduction: { percent: true, sign: -1 },

  characterAttackSpeedModifier: { percent: true, sign: 1 },
  characterSpellCastSpeedModifier: { percent: true, sign: 1 },
  characterRunSpeedModifier: { percent: true, sign: 1 },
  characterRunSpeedMaxModifier: { percent: true, sign: 1 },
  characterTotalSpeedModifier: { percent: true, sign: 1 },
  characterConstitutionModifier: { percent: true, sign: 1 },
  // Value-suffix game format ("Healing Effects Increased by {v}%") - the value cannot be stripped to a
  // clean prefix label across languages, so the label is app-authored (see stat.override.<id>).
  characterHealIncreasePercent: { percent: true, sign: 1 },
  characterDodgePercent: { percent: true, sign: 1 },
  characterDeflectProjectile: { percent: true, sign: 1 },
  characterEnergyAbsorptionPercent: { percent: true, sign: 1 },
  characterDefensiveBlockRecoveryReduction: { percent: true, sign: -1 },

  // Attribute/requirement reductions (official game labels; shown as a negative percent).
  characterArmorStrengthReqReduction: { percent: true, sign: -1 },
  characterMeleeStrengthReqReduction: { percent: true, sign: -1 },
  characterShieldStrengthReqReduction: { percent: true, sign: -1 },
  characterMeleeDexterityReqReduction: { percent: true, sign: -1 },
  characterHuntingDexterityReqReduction: { percent: true, sign: -1 },
  characterWeaponIntelligenceReqReduction: { percent: true, sign: -1 },
  characterJewelryIntelligenceReqReduction: { percent: true, sign: -1 },
};

function humanize(id: string): string {
  const s = id
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._]/g, " ")
    .replace(/^character /i, "") // "Character" prefix is redundant in the planner
    .replace(/\bStrength\b/g, "Physique")
    .replace(/\bDexterity\b/g, "Cunning")
    .replace(/\bIntelligence\b/g, "Spirit")
    .replace(/\bMana\b/g, "Energy")
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function classify(id: string): Classified | null {
  if (/^[A-Z]/.test(id)) return null; // weapon-class token (Spear2h, Dagger, ...) - shown via weapon requirement
  const o = OVERRIDES[id];
  if (o) return { label: appT(`stat.override.${id}`), percent: o.percent, sign: o.sign };

  let m: RegExpMatchArray | null;

  // Offensive damage-over-time: offensiveSlow<Type>[Duration][Modifier|Min|Max]
  if ((m = id.match(/^offensiveSlow([A-Za-z]+?)(Duration)?(Modifier|Min|Max)?$/))) {
    const type = dotDamageLabel(m[1]!);
    if (type) {
      const percent = m[3] === "Modifier";
      const label = m[2] ? appT("stat.template.duration", { type }) : appT("stat.template.damage", { type });
      return { label, percent, sign: 1 };
    }
  }
  // Offensive instant damage: offensive<Type>[Modifier|Min|Max]
  if ((m = id.match(/^offensive([A-Za-z]+?)(Modifier|Min|Max)?$/))) {
    const type = instantDamageLabel(m[1]!);
    if (type) return { label: appT("stat.template.damage", { type }), percent: m[2] === "Modifier", sign: 1 };
  }
  // Defensive maximum resistance: defensive<Type>MaxResist
  if ((m = id.match(/^defensive([A-Za-z]+?)MaxResist$/))) {
    const type = resistLabel(m[1]!);
    if (type) return { label: appT("stat.template.maxResistance", { type }), percent: true, sign: 1 };
  }
  // Defensive reduced damage-over-time duration: defensive<Type>Duration.
  // GD names these by the DoT (Internal Trauma/Burn/Frostburn/...) and shows them as a positive percent.
  if ((m = id.match(/^defensive([A-Za-z]+?)Duration$/))) {
    const type = dotDamageLabel(m[1]!);
    if (type) return { label: appT("stat.template.reducedDuration", { type }), percent: true, sign: 1 };
  }
  // Stacking resistance reduction on the target, as named by powerStatBenefit.
  if ((m = id.match(/^offensiveTarget([A-Za-z]+?)ResistanceReduction$/))) {
    const type = resistLabel(m[1]!);
    if (type) return { label: appT("stat.template.resistanceReduction", { type }), percent: true, sign: 1 };
  }
  // Defensive base resistance: defensive<Type>
  if ((m = id.match(/^defensive([A-Za-z]+?)$/))) {
    const type = resistLabel(m[1]!);
    if (type) return { label: appT("stat.template.resistance", { type }), percent: true, sign: 1 };
  }
  // Retaliation damage: retaliation<Type>[Modifier|Min|Max]. Modifier is the percent form.
  if ((m = id.match(/^retaliation([A-Za-z]+?)(Modifier|Min|Max)?$/))) {
    const type = instantDamageLabel(m[1]!);
    if (type) return { label: appT("stat.template.retaliation", { type }), percent: m[2] === "Modifier", sign: 1 };
  }
  // Character attribute: character<Attr>[Modifier]
  if ((m = id.match(/^character([A-Za-z]+?)(Modifier)?$/))) {
    const name = attrLabel(m[1]!);
    if (name) return { label: name, percent: m[2] === "Modifier", sign: 1 };
  }

  // Value-embedded game format stats ("{v}% <noun>"): source the noun from the game tag, strip the value.
  const fmtTag = STAT_FORMAT_TAGS[id];
  if (fmtTag) return { label: gameStrippedT(fmtTag), percent: true, sign: 1 };

  // Fallback: humanize, treating Modifier/Percent/Resistance/Chance as percent and Reduction as a negative percent.
  const percent = /Modifier$|Percent|Reduction$|Resistance$|Chance$/.test(id);
  return { label: litT(humanize(id)), percent, sign: /Reduction$/.test(id) ? -1 : 1 };
}

function fmtValue(value: number, percent: boolean, sign: number): Text {
  const n = sign * value;
  const s = n >= 0 ? `+${n}` : `${n}`;
  return litT(percent ? `${s}%` : s);
}

// Internal race name -> player-facing (GD shows the plural, except Undead). Display names and the
// multi-race join separator live in the catalog under stat.race.<Race> / stat.race.join.
const RACE_SEGMENTS = new Set(["Beast", "Chthonic", "Human", "Undead"]);
function raceLabel(targets?: string[]): Text | null {
  if (!targets || targets.length === 0) return null;
  const parts: Text[] = [];
  targets.forEach((t, i) => {
    if (i > 0) parts.push(appT("stat.race.join"));
    parts.push(RACE_SEGMENTS.has(t) ? appT(`stat.race.${t}`) : litT(t));
  });
  return { k: "join", parts };
}

/** Format a single stat id + value into a display row, or null if it is not a stat (weapon token). */
export function statRow(id: string, value: number, racialTarget?: string[]): StatRow | null {
  const c = classify(id);
  if (!c) return null;
  let label = c.label;
  const race = raceLabel(racialTarget);
  if (race) {
    if (id === "racialBonusPercentDamage") label = appT("stat.subject.damageToRace", { race });
    else if (id === "racialBonusPercentDefense") label = appT("stat.subject.lessDamageFromRace", { race });
  }
  return { label, value: fmtValue(value, c.percent, c.sign) };
}

// Display groups for the benefits sidebar, in render order. Offense-side debuff sections
// (Resistance Reduction, Crowd Control, Retaliation) and the three-way Defense split keep the
// high-value concepts from being buried in one giant section. Routing lives in groupFor.
// These act as internal identifiers (Map keys in this file and in benefitRows.ts, and asserted
// directly by statFormat.test.ts / condense.test.ts), so they stay plain English here. The
// sidebar's rendered header resolves the display text via GROUP_KEY + translate (see
// sidebarView.ts), never this raw value.
export const GROUP_ORDER = [
  "Attributes",
  "Offense",
  "Resistance Reduction",
  "Crowd Control",
  "Retaliation",
  "Resistances",
  "Status Protection",
  "Armor & Mitigation",
  "Other",
] as const;
export type StatGroup = (typeof GROUP_ORDER)[number];

// Maps a StatGroup identifier to its catalog key, for the sidebar header render site.
export const GROUP_KEY: Record<StatGroup, string> = {
  Attributes: "stat.group.attributes",
  Offense: "stat.group.offense",
  "Resistance Reduction": "stat.group.resistanceReduction",
  "Crowd Control": "stat.group.crowdControl",
  Retaliation: "stat.group.retaliation",
  Resistances: "stat.group.resistances",
  "Status Protection": "stat.group.statusProtection",
  "Armor & Mitigation": "stat.group.armorAndMitigation",
  Other: "stat.group.other",
};

function groupFor(id: string): StatGroup {
  if (id === "racialBonusPercentDamage") return "Offense";
  if (id === "racialBonusPercentDefense") return "Armor & Mitigation";
  if (/^retaliation/.test(id)) return "Retaliation";
  if (/ResistanceReduction/.test(id) || /^offensivePhysicalReductionPercent/.test(id)) return "Resistance Reduction";
  if (
    /^offensive(Stun|Freeze|Petrify|Knockdown|Confusion|Fumble|ProjectileFumble|SlowRunSpeed|SlowTotalSpeed|SlowAttackSpeed|SlowOffensiveAbility|SlowDefensiveAbility|TotalDamageReductionPercent|TargetArmorReduction)/.test(
      id,
    )
  )
    return "Crowd Control";
  if (/^offensive/.test(id)) return "Offense";
  // Defensive split. Order matters: damage-type resistances first, then the
  // status/effect protections, then everything else defensive (armor, block, reflect).
  if (
    /^defensive(Physical|Pierce|Fire|Cold|Lightning|Aether|Chaos|Poison|Life|Bleeding)(MaxResist)?$/.test(id) ||
    id === "defensiveElementalResistance"
  )
    return "Resistances";
  if (/^defensive(Physical|Fire|Cold|Lightning|Poison|Life|Bleeding)Duration$/.test(id)) return "Status Protection";
  if (/^defensive(Stun|Freeze|Petrify|Trap|Disruption)$/.test(id)) return "Status Protection";
  if (id === "defensiveTotalSpeedResistance" || /^defensiveSlow(Life|Mana)Leach/.test(id)) return "Status Protection";
  if (/^defensive/.test(id)) return "Armor & Mitigation";
  if (/^character/.test(id)) return "Attributes";
  return "Other";
}

/** Whether a raw stat id belongs in the benefit filter vocabulary: any group except "Other".
 *  Used to admit recognized celestial-power stats and exclude ability-meta (cooldown, projectiles, etc.). */
export function isFilterableStat(id: string): boolean {
  return groupFor(id) !== "Other";
}

// Build display rows paired with a representative stat id (for grouping). Merges
// flat <base>Min/<base>Max damage pairs into one "+min-max" row, drops weapon tokens.
function bonusEntries(
  bonuses: Record<string, number>,
  opts: { racialTarget?: string[] },
): { id: string; row: StatRow }[] {
  const used = new Set<string>();
  const out: { id: string; row: StatRow }[] = [];
  for (const k of Object.keys(bonuses)) {
    if (used.has(k)) continue;
    const mm = k.match(/^(.*)(Min|Max)$/);
    if (mm) {
      const minK = `${mm[1]}Min`;
      const maxK = `${mm[1]}Max`;
      if (minK in bonuses && maxK in bonuses) {
        const c = classify(minK);
        if (c && !c.percent) {
          used.add(minK);
          used.add(maxK);
          out.push({ id: minK, row: { label: c.label, value: litT(`+${bonuses[minK]}-${bonuses[maxK]}`) } });
          continue;
        }
      }
    }
    const r = statRow(k, bonuses[k]!, opts.racialTarget);
    used.add(k);
    if (r) out.push({ id: k, row: r });
  }
  return out;
}

/** Format a bonuses map into a single list of display rows, in stable input order. */
export function formatBonusRows(bonuses: Record<string, number>, opts: { racialTarget?: string[] } = {}): StatRow[] {
  return bonusEntries(bonuses, opts).map((e) => e.row);
}

/** Like formatBonusRows, but each row keeps its representative stat id (for tagging tooltip rows). */
export function formatBonusRowsWithIds(
  bonuses: Record<string, number>,
  opts: { racialTarget?: string[] } = {},
): { id: string; label: Text; value: Text }[] {
  return bonusEntries(bonuses, opts).map((e) => ({ id: e.id, label: e.row.label, value: e.row.value }));
}

// A grouped row keeps its representative stat id so callers can diff it (highlight changes).
export interface GroupedRow {
  id: string;
  label: Text;
  value: Text;
}

// --- Celestial power ability stats ------------------------------------------
// A devotion power's tooltip is not a list of "+N%" bonuses; it is the ability's
// own stat lines the way grimtools shows them ("1.5 Second Skill Recharge",
// "1125 Poison Damage over 5 Seconds"). These render with the same value+label
// shape as bonus rows, but the value carries the unit and no leading sign.
// `rows` carry grimtools' semantic order and must never be re-sorted; `fallthrough`
// holds the leftover stats in stable input order, and adapters sort that segment
// alphabetically by resolved label before appending it.
export interface PowerRows {
  rows: StatRow[];
  fallthrough: StatRow[];
}

function fmtNum(n: number): string {
  return String(n);
}

// Shared " for N Seconds" suffix used by several ability-debuff lines below and by formatPet.
function forSecondsSuffix(seconds: number): Text {
  return appT("stat.power.forSeconds", { seconds: fmtNum(seconds) });
}

// Offensive/Defensive Ability debuffs (e.g. Scorpion Sting's reduced DA).
const ABILITY_DEBUFFS: [string, string][] = [
  ["DefensiveAbility", "stat.power.reducedDefensiveAbility"],
  ["OffensiveAbility", "stat.power.reducedOffensiveAbility"],
];

// Other timed target debuffs: a percent movement slow, plus flat/percent resistance
// and damage reductions (which lack the "Slow" infix the ability debuffs use).
const TIMED_DEBUFFS: [string, string, string, boolean][] = [
  ["offensiveSlowRunSpeedMin", "offensiveSlowRunSpeedDurationMin", "stat.power.slowerTargetMovement", true],
  [
    "offensiveTotalResistanceReductionAbsoluteMin",
    "offensiveTotalResistanceReductionAbsoluteDurationMin",
    "stat.power.reducedTargetResistances",
    false,
  ],
  [
    "offensiveTotalDamageReductionPercentMin",
    "offensiveTotalDamageReductionPercentDurationMin",
    "stat.power.reducedTargetDamage",
    true,
  ],
  // Magnitude + duration status debuffs, reusing the condensed-view subject vocabulary.
  ["offensiveFumbleMin", "offensiveFumbleDurationMin", "stat.subject.fumble", true],
  ["offensiveProjectileFumbleMin", "offensiveProjectileFumbleDurationMin", "stat.subject.impairedAim", true],
  ["offensiveSlowAttackSpeedMin", "offensiveSlowAttackSpeedDurationMin", "stat.subject.slowAttackSpeed", true],
  ["offensiveSlowTotalSpeedMin", "offensiveSlowTotalSpeedDurationMin", "stat.subject.slowTotalSpeed", true],
  [
    "offensiveElementalResistanceReductionAbsoluteMin",
    "offensiveElementalResistanceReductionAbsoluteDurationMin",
    "stat.subject.reducedElementalResistancesFlat",
    false,
  ],
  [
    "offensivePhysicalReductionPercentMin",
    "offensivePhysicalReductionPercentDurationMin",
    "stat.subject.reducedPhysicalResistance",
    true,
  ],
];

// A power's timed effects, keyed by the duration stat: the damage-over-time tick lengths and the
// debuff timers formatPowerStats folds into a magnitude line ("for N seconds").
const POWER_EFFECT_DURATION_IDS: ReadonlySet<string> = new Set([
  ...[...DOT_DAMAGE_SEGMENTS].map((seg) => `offensiveSlow${seg}DurationMin`),
  ...ABILITY_DEBUFFS.map(([seg]) => `offensiveSlow${seg}DurationMin`),
  ...TIMED_DEBUFFS.map(([, durK]) => durK),
]);

/**
 * Whether a stat id is, on a celestial power, the length of the power's own effect rather than a
 * benefit it grants: a proc that ticks Frostburn over 2 seconds does not extend Frostburn duration.
 * On a power these ids never light a star or take a tag; the ones no star bonus carries stay in the
 * URL vocabulary as deprecated tags (see urlState's deprecatedBenefitIds).
 */
export function isPowerEffectDuration(id: string): boolean {
  return POWER_EFFECT_DURATION_IDS.has(id);
}

// Target debuffs a power writes as a negative player-side stat on the enemy, mapped to the
// benefit id that names the same debuff where a power states it directly.
const NEGATIVE_TARGET_DEBUFFS: Record<string, string> = {
  characterOffensiveAbility: "offensiveSlowOffensiveAbilityMin",
  characterRunSpeedModifier: "offensiveSlowRunSpeedMin",
  defensiveProtectionModifier: "offensiveTargetArmorReduction",
};

/**
 * The benefit a celestial power stat grants, as the tag id it answers and the magnitude that tag
 * weighs, or null when the stat is not a benefit. A power's own effect timers are not benefits. A
 * negative value is a debuff on the enemy that reuses the player-side id: "-32% Physical
 * Resistance" is defensivePhysical: -32 applied to the target, a stacking resistance reduction
 * (docs/resistance-reduction.md), so it answers offensiveTargetPhysicalResistanceReduction and
 * never the player's Physical Resistance. A negative stat with no target-side meaning is dropped.
 */
export function powerStatBenefit(id: string, value: number): { id: string; value: number } | null {
  if (isPowerEffectDuration(id)) return null;
  if (value >= 0) return isFilterableStat(id) ? { id, value } : null;
  const m = id.match(/^defensive([A-Za-z]+)$/);
  if (m && RESIST_SEGMENTS.has(m[1]!)) return { id: `offensiveTarget${m[1]!}ResistanceReduction`, value: -value };
  const debuff = NEGATIVE_TARGET_DEBUFFS[id];
  return debuff ? { id: debuff, value: -value } : null;
}

/**
 * Format a celestial power's level-selected stat map into GD-style ability rows.
 * Ability meta fields (recharge, projectiles, pass-through, radius, weapon %) and
 * the duration-paired damage-over-time / debuff lines are rendered explicitly, in
 * grimtools' order; any remaining raw stat ids fall through to bonus formatting
 * (sign stripped, since an ability grants the value rather than a +/- modifier).
 */
export function formatPowerStats(stats: Record<string, number>): PowerRows {
  const rows: StatRow[] = [];
  const used = new Set<string>();
  const take = (k: string): number | undefined => {
    if (k in stats) {
      used.add(k);
      return stats[k];
    }
    return undefined;
  };

  const cd = take("skillCooldownTime");
  if (cd !== undefined) rows.push({ value: litT(fmtNum(cd)), label: appT("stat.power.secondSkillRecharge") });

  const dur = take("skillActiveDuration");
  if (dur !== undefined) rows.push({ value: litT(fmtNum(dur)), label: appT("stat.power.secondDuration") });

  const proj = take("projectileLaunchNumber");
  if (proj !== undefined) rows.push({ value: litT(fmtNum(proj)), label: appT("stat.power.projectiles") });

  const pierce = take("projectilePiercingChance");
  if (pierce !== undefined) rows.push({ value: litT(`${fmtNum(pierce)}%`), label: appT("stat.power.passThrough") });

  const radius = take("projectileExplosionRadius") ?? take("skillTargetRadius");
  if (radius !== undefined) rows.push({ value: litT(fmtNum(radius)), label: appT("stat.power.meterRadius") });
  // When an explosion radius already supplied the line, consume the skill's internal target-selection
  // radius too, so it does not fall through to humanize() as a raw "Skill Target Radius" line.
  take("skillTargetRadius");

  const absorb = take("damageAbsorption");
  if (absorb !== undefined) rows.push({ value: litT(fmtNum(absorb)), label: appT("stat.power.damageAbsorption") });

  // Heal / restore procs (Dryad's Blessing, Giant's Blood, Inspiration): a flat and a
  // percent health restore, plus a percent energy restore. Value carries the unit.
  const healFlat = take("skillLifeBonus");
  if (healFlat !== undefined) rows.push({ value: litT(fmtNum(healFlat)), label: appT("stat.power.healthRestored") });
  const healPct = take("skillLifePercent");
  if (healPct !== undefined)
    rows.push({ value: litT(`${fmtNum(healPct)}%`), label: appT("stat.power.healthRestored") });
  const energyPct = take("skillManaPercent");
  if (energyPct !== undefined)
    rows.push({ value: litT(`${fmtNum(energyPct)}%`), label: appT("stat.power.energyRestored") });

  const weapon = take("weaponDamagePct");
  if (weapon !== undefined) rows.push({ value: litT(`${fmtNum(weapon)}%`), label: appT("stat.power.weaponDamage") });

  // Damage-over-time: offensiveSlow<Type>Min holds per-second damage paired with a
  // duration; grimtools shows the total over the listed duration.
  for (const seg of DOT_DAMAGE_SEGMENTS) {
    const minK = `offensiveSlow${seg}Min`;
    const durK = `offensiveSlow${seg}DurationMin`;
    if (minK in stats && durK in stats) {
      used.add(minK);
      used.add(durK);
      const total = Math.round(stats[minK]! * stats[durK]!);
      rows.push({
        id: minK,
        value: litT(fmtNum(total)),
        label: appT("stat.power.dotDamageOverSeconds", {
          name: statLabel(`stat.dot.${seg}`),
          seconds: fmtNum(stats[durK]!),
        }),
      });
    }
  }
  for (const [seg, key] of ABILITY_DEBUFFS) {
    const minK = `offensiveSlow${seg}Min`;
    const durK = `offensiveSlow${seg}DurationMin`;
    if (minK in stats) {
      used.add(minK);
      const dur = stats[durK];
      if (durK in stats) used.add(durK);
      const label = dur !== undefined ? joinT(appT(key), forSecondsSuffix(dur)) : appT(key);
      rows.push({ id: minK, value: litT(fmtNum(stats[minK]!)), label });
    }
  }
  for (const [minK, durK, key, pct] of TIMED_DEBUFFS) {
    if (minK in stats) {
      used.add(minK);
      const dur = stats[durK];
      if (durK in stats) used.add(durK);
      const label = dur !== undefined ? joinT(appT(key), forSecondsSuffix(dur)) : appT(key);
      const v = stats[minK]!;
      rows.push({ id: minK, value: litT(pct ? `${fmtNum(v)}%` : fmtNum(v)), label });
    }
  }

  // Crowd-control procs (Stun/Freeze/Petrify/Knockdown/Confusion): a duration (Min, with an optional
  // Min-Max range) and an optional Chance. Rendered grimtools-style ("50% Chance of 1 Seconds of Stun",
  // or "1.8 Seconds of Confusion" when guaranteed), reusing the condensed-view stat.subject.cc* nouns.
  const ccEffects: [string, string][] = [
    ["Stun", "stat.subject.ccStun"],
    ["Freeze", "stat.subject.ccFreeze"],
    ["Petrify", "stat.subject.ccPetrify"],
    ["Knockdown", "stat.subject.ccKnockdown"],
    ["Confusion", "stat.subject.ccConfusion"],
  ];
  for (const [seg, key] of ccEffects) {
    const minK = `offensive${seg}Min`;
    if (!(minK in stats)) continue;
    const min = take(minK)!;
    const max = take(`offensive${seg}Max`);
    const chance = take(`offensive${seg}Chance`);
    const seconds = max !== undefined && max !== min ? `${fmtNum(min)}-${fmtNum(max)}` : fmtNum(min);
    if (chance !== undefined)
      rows.push({
        id: minK,
        value: litT(`${fmtNum(chance)}%`),
        label: appT("stat.power.ccChanceDuration", { seconds, effect: appT(key) }),
      });
    else rows.push({ id: minK, value: litT(seconds), label: appT("stat.power.ccDuration", { effect: appT(key) }) });
  }

  // Anything else (instant damage ranges, leech, resist reductions): reuse the
  // bonus formatter and drop the leading "+" an ability line does not show. These
  // stay in stable input order here; adapters sort them by resolved label.
  const rest: Record<string, number> = {};
  for (const k of Object.keys(stats)) if (!used.has(k)) rest[k] = stats[k]!;
  const fallthrough: StatRow[] = [];
  for (const r of formatBonusRowsWithIds(rest)) {
    const v = r.value.k === "lit" ? litT(r.value.s.replace(/^\+/, "")) : r.value;
    fallthrough.push({ id: r.id, label: r.label, value: v });
  }
  return { rows, fallthrough };
}

/**
 * A summon proc's pet: a "Summons N <Pet> for M Seconds" summary line plus the pet's
 * base attack rendered as ability stat rows (reusing the power-stat formatter).
 */
export function formatPet(pet: PetInfo): { summon: Text; attack: PowerRows } {
  const plural = (pet.count ?? 1) > 1;
  const num = plural ? `${fmtNum(pet.count!)} ` : "";
  const nameBase: Text = pet.nameTag ? gameT(pet.nameTag) : appT("stat.pet.minion");
  const name: Text = plural ? joinT(nameBase, "s") : nameBase;
  const dur: Text = pet.duration ? forSecondsSuffix(pet.duration) : litT("");
  return { summon: appT("stat.pet.summons", { num, name, dur }), attack: formatPowerStats(pet.attackStats) };
}

// --- Condensed view: one line per concept (subject), carrying its dimensions ---
// A "subject" is a damage type, a resistance type, an attribute, or a standalone
// stat. Its dimensions (flat, percent, max-resist, duration flat/percent) collapse
// onto that one subject so a concept is not spread across several rows.
export type StatDim = "flat" | "pct" | "max" | "durFlat" | "durPct";
// Flat before percent: the flat value is added first, then the percent applies.
const DIM_ORDER: StatDim[] = ["flat", "pct", "max", "durFlat", "durPct"];

export interface CondensedPart {
  dim: StatDim;
  value: Text;
  id: string;
}
export interface CondensedSubject {
  subject: Text;
  key: string;
  parts: CondensedPart[];
}
export interface CondensedGroup {
  group: StatGroup;
  subjects: CondensedSubject[];
}

// Map a raw stat id to its (group, subject, dimension), mirroring classify's families. subjectKey is
// the locale-independent identity used to merge dimensions of the same concept onto one subject
// (see CondensedSubject.key = `${group}:${subjectKey}`); subject is the Text label shown to the user.
function decompose(id: string): { group: StatGroup; subjectKey: string; subject: Text; dim: StatDim } | null {
  const c = classify(id);
  if (!c) return null;
  const group = groupFor(id);
  let m: RegExpMatchArray | null;
  if ((m = id.match(/^offensiveSlow([A-Za-z]+?)(Duration)?(Modifier|Min|Max)?$/))) {
    const type = dotDamageLabel(m[1]!);
    if (type) {
      const pct = m[3] === "Modifier";
      const dim: StatDim = m[2] ? (pct ? "durPct" : "durFlat") : pct ? "pct" : "flat";
      return { group, subjectKey: `dot:${m[1]!}`, subject: type, dim };
    }
  }
  if ((m = id.match(/^offensive([A-Za-z]+?)(Modifier|Min|Max)?$/))) {
    const type = instantDamageLabel(m[1]!);
    if (type) return { group, subjectKey: `damage:${m[1]!}`, subject: type, dim: m[2] === "Modifier" ? "pct" : "flat" };
  }
  // Retaliation damage: retaliation<Type>[Modifier|Min|Max], mirrors classify's family (see there).
  if ((m = id.match(/^retaliation([A-Za-z]+?)(Modifier|Min|Max)?$/))) {
    const type = instantDamageLabel(m[1]!);
    if (type)
      return {
        group,
        subjectKey: `retaliation:${m[1]!}`,
        subject: appT("stat.template.retaliation", { type }),
        dim: m[2] === "Modifier" ? "pct" : "flat",
      };
  }
  if ((m = id.match(/^defensive([A-Za-z]+?)MaxResist$/))) {
    const type = resistLabel(m[1]!);
    if (type)
      return {
        group,
        subjectKey: `resist:${m[1]!}`,
        subject: appT("stat.template.resistance", { type }),
        dim: "max",
      };
  }
  if ((m = id.match(/^defensive([A-Za-z]+?)$/))) {
    const type = resistLabel(m[1]!);
    if (type)
      return {
        group,
        subjectKey: `resist:${m[1]!}`,
        subject: appT("stat.template.resistance", { type }),
        dim: "pct",
      };
  }
  if ((m = id.match(/^character([A-Za-z]+?)(Modifier)?$/))) {
    const name = attrLabel(m[1]!);
    if (name) return { group, subjectKey: `attr:${m[1]!}`, subject: name, dim: m[2] ? "pct" : "flat" };
  }
  // Intentional merges: these two id pairs share one subject even though they are not the same
  // stat family (armor's flat/pct facets and the fear status effect's magnitude/chance facets).
  if (id === "defensiveProtection" || id === "defensiveProtectionModifier")
    return {
      group,
      subjectKey: "armor",
      subject: appT("stat.override.defensiveProtection"),
      dim: id.endsWith("Modifier") ? "pct" : "flat",
    };
  if (id === "retaliationFearMin" || id === "retaliationFearChance")
    return {
      group,
      subjectKey: "retaliation-fear",
      subject: appT("stat.override.retaliationFearMin"),
      dim: id === "retaliationFearChance" ? "pct" : "flat",
    };
  // Resistance reduction: flat and percent are distinct subjects (they stack differently in game).
  if (id.match(/^offensiveTotalResistanceReductionAbsolute(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.power.reducedTargetResistances",
      subject: appT("stat.power.reducedTargetResistances"),
      dim: /Duration/.test(id) ? "durFlat" : "flat",
    };
  if (id.match(/^offensiveElementalResistanceReductionAbsolute(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.subject.reducedElementalResistancesFlat",
      subject: appT("stat.subject.reducedElementalResistancesFlat"),
      dim: /Duration/.test(id) ? "durFlat" : "flat",
    };
  if (id.match(/^offensiveElementalResistanceReductionPercent(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.override.offensiveElementalResistanceReductionPercentMin",
      subject: appT("stat.override.offensiveElementalResistanceReductionPercentMin"),
      dim: /Duration/.test(id) ? "durFlat" : "pct",
    };
  if (id.match(/^offensivePhysicalReductionPercent(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.subject.reducedPhysicalResistance",
      subject: appT("stat.subject.reducedPhysicalResistance"),
      dim: /Duration/.test(id) ? "durFlat" : "pct",
    };
  // Crowd control: a status effect (magnitude Min + a Chance facet).
  let cc: RegExpMatchArray | null;
  if ((cc = id.match(/^offensive(Stun|Freeze|Petrify|Knockdown|Confusion)(Chance)?(Min|Max)?$/)))
    return {
      group,
      subjectKey: `stat.subject.cc${cc[1]}`,
      subject: appT(`stat.subject.cc${cc[1]}`),
      dim: cc[2] ? "pct" : "flat",
    };
  if (id.match(/^offensiveFumble(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.subject.fumble",
      subject: appT("stat.subject.fumble"),
      dim: /Duration/.test(id) ? "durFlat" : "flat",
    };
  if (id.match(/^offensiveProjectileFumble(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.subject.impairedAim",
      subject: appT("stat.subject.impairedAim"),
      dim: /Duration/.test(id) ? "durFlat" : "flat",
    };
  if (id.match(/^offensiveSlowRunSpeed(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.subject.slowMovement",
      subject: appT("stat.subject.slowMovement"),
      dim: /Duration/.test(id) ? "durFlat" : "pct",
    };
  if (id.match(/^offensiveSlowTotalSpeed(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.subject.slowTotalSpeed",
      subject: appT("stat.subject.slowTotalSpeed"),
      dim: /Duration/.test(id) ? "durFlat" : "pct",
    };
  if (id.match(/^offensiveSlowAttackSpeed(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.subject.slowAttackSpeed",
      subject: appT("stat.subject.slowAttackSpeed"),
      dim: /Duration/.test(id) ? "durFlat" : "pct",
    };
  if (id.match(/^offensiveSlowOffensiveAbility(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.power.reducedOffensiveAbility",
      subject: appT("stat.power.reducedOffensiveAbility"),
      dim: /Duration/.test(id) ? "durFlat" : "flat",
    };
  if (id.match(/^offensiveSlowDefensiveAbility(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.power.reducedDefensiveAbility",
      subject: appT("stat.power.reducedDefensiveAbility"),
      dim: /Duration/.test(id) ? "durFlat" : "flat",
    };
  if (id.match(/^offensiveTotalDamageReductionPercent(Duration)?Min$/))
    return {
      group,
      subjectKey: "stat.power.reducedTargetDamage",
      subject: appT("stat.power.reducedTargetDamage"),
      dim: /Duration/.test(id) ? "durFlat" : "pct",
    };
  // Standalone stat: its own one-line subject, keyed by its raw stat id.
  return { group, subjectKey: id, subject: c.label, dim: c.percent ? "pct" : "flat" };
}

/** Format a bonuses map into subjects grouped by category, each subject carrying its dimensions. */
export function condensedRows(
  bonuses: Record<string, number>,
  opts: { racialTarget?: string[] } = {},
): CondensedGroup[] {
  const groups = new Map<StatGroup, Map<string, CondensedSubject>>();
  for (const { id, row } of bonusEntries(bonuses, opts)) {
    const d = decompose(id);
    if (!d) continue;
    let subs = groups.get(d.group);
    if (!subs) {
      subs = new Map();
      groups.set(d.group, subs);
    }
    let cs = subs.get(d.subjectKey);
    if (!cs) {
      cs = { subject: d.subject, key: `${d.group}:${d.subjectKey}`, parts: [] };
      subs.set(d.subjectKey, cs);
    }
    cs.parts.push({ dim: d.dim, value: row.value, id });
  }
  return GROUP_ORDER.filter((g) => groups.has(g)).map((g) => ({
    group: g,
    subjects: [...groups.get(g)!.values()].map((cs) => ({
      ...cs,
      parts: cs.parts.sort((a, b) => DIM_ORDER.indexOf(a.dim) - DIM_ORDER.indexOf(b.dim)),
    })),
  }));
}

/** Format a bonuses map into display rows grouped by category, in GROUP_ORDER. */
export function groupedBonusRows(
  bonuses: Record<string, number>,
  opts: { racialTarget?: string[] } = {},
): { group: StatGroup; rows: GroupedRow[] }[] {
  const byGroup = new Map<StatGroup, GroupedRow[]>();
  for (const { id, row } of bonusEntries(bonuses, opts)) {
    const g = groupFor(id);
    const arr = byGroup.get(g) ?? [];
    arr.push({ id, label: row.label, value: row.value });
    byGroup.set(g, arr);
  }
  return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
    group: g,
    rows: byGroup.get(g)!,
  }));
}
